/**
 * Wire a {@link LatencyEnv} from a ResolvedModel — the adapter between the pure
 * latency fold (process-latency.ts) and the resolved model. Builds the
 * interface-path → `latency` index, reuses `walkerContextFor` for the
 * `do`-splice subprocess/process maps and `swimlaneContextFor` for actor
 * detection.
 */

import { parseDuration } from "@archlang/calc";
import type { QualifiedName, Value } from "./types.js";
import type { ResolvedModel, ResolvedInterface } from "./resolver.js";
import type { ProcessModel } from "./process-model.js";
import type { LatencyEnv, LatencyLookup } from "./process-latency.js";
import { walkerContextFor } from "./process-contexts.js";
import { buildIndexes } from "./graph.js";
import { resolveScoped } from "./hoist-index.js";
import { estimateProcess, latencyGetters, type ProcessLatencyResolver } from "./process-latency.js";

/** Read an interface's authored `latency` field as a lookup result (seconds). */
export function latencyOfInterface(iface: ResolvedInterface): LatencyLookup {
  const f = iface.body.fields.find((x) => x.path.length === 1 && x.path[0] === "latency");
  if (!f) return { kind: "missing" };
  const raw = scalarText(f.value);
  if (raw === undefined) return { kind: "malformed", raw: "" };
  const parsed = parseDuration(raw);
  if (parsed.ok) return { kind: "value", lo: parsed.lo, hi: parsed.hi };
  if (parsed.reason === "empty") return { kind: "missing" };
  return { kind: "malformed", raw };
}

function scalarText(v: Value | undefined): string | undefined {
  if (!v) return undefined;
  if (v.kind === "identifier") return v.text;
  if (v.kind === "string") return v.text;
  return undefined;
}

/**
 * Build a LatencyEnv for one process. The interface lookup goes through the
 * resolver's spelling-complete index (`interfacesByPath` — full + §4.11 elided
 * surface-leaf + hoisted-suffix + default-interface spellings), so a callee
 * written in any canonical short form resolves to the same interface an edge
 * would; the subprocess/process splice maps are process-scoped
 * (`walkerContextFor`).
 */
export function buildLatencyEnv(
  model: ResolvedModel,
  proc: ProcessModel,
  indexes: ReturnType<typeof buildIndexes> = buildIndexes(model),
): LatencyEnv {
  const walkerCtx = walkerContextFor(model, proc);
  const cache = new Map<string, LatencyLookup>();
  // Multi-space referrer context — a callee is written space-relative
  // (`Auth.read`) but indexed by full path (`SpaceA.Auth.read`); resolve it
  // against the process's space chain EXACTLY as the edge builder does, so the
  // latency lookup can't miss an interface a real edge resolves.
  const referrerFile = proc.source?.file;
  const referrerSpace = referrerFile ? indexes.spaces?.get(referrerFile) : undefined;
  const latencyOf = (interfacePath: QualifiedName): LatencyLookup => {
    const key = interfacePath.join(".");
    const hit = cache.get(key);
    if (hit) return hit;
    let iface: ResolvedInterface | undefined;
    // Space-scoped candidates OVERRIDE the global index (which, for an
    // ambiguous suffix, holds an arbitrary last-wins entry pointing at the
    // WRONG space) — exactly as the edge builder overrides `fromModule`/target.
    const scoped = indexes.scopedIfaceOwners?.get(key);
    if (scoped && scoped.length > 0) {
      const ro = resolveScoped(scoped, referrerSpace);
      if (ro && ro !== "ambiguous") iface = ro.value.iface;
    }
    if (!iface) iface = indexes.interfacesByPath.get(key);
    const look: LatencyLookup = iface ? latencyOfInterface(iface) : { kind: "missing" };
    cache.set(key, look);
    return look;
  };
  return {
    latencyOf,
    subprocessByName: walkerCtx.subprocessByName,
    processByName: walkerCtx.processByName,
  };
}

/**
 * A {@link ProcessLatencyResolver} over a whole model: subject key (a process's
 * qualified name) → its latency getters, computed once and cached. Injected on
 * the EvalContext so `@estimatedLatency[.…]` evaluates in `where`/policy
 * expressions. A non-process (or anonymous) key resolves to undefined (neutral).
 */
export function buildProcessLatencyResolver(model: ResolvedModel): ProcessLatencyResolver {
  // Register processes FIRST and never overwrite, so a process and a same-owner
  // subprocess of the same name resolve to the PROCESS — matching the node
  // universe's first-wins rule (a mismatch would silently return the wrong
  // subject's estimate).
  const byKey = new Map<string, { model: ProcessModel; fragment: boolean }>();
  const put = (path: string, m: ProcessModel, fragment: boolean): void => {
    if (!byKey.has(path)) byKey.set(path, { model: m, fragment });
  };
  for (const pm of model.processModels ?? []) {
    if (pm.name) put([...(pm.ownerPath ?? []), pm.name].join("."), pm, false);
  }
  for (const sm of model.subprocessModels ?? []) {
    // A subprocess is always a spliced fragment, so it is estimated WITHOUT the
    // start-trigger split (a leading `await` is a real mid-flow wait, matching
    // the `do`-splice demotion).
    if (sm.name) put([...(sm.ownerPath ?? []), sm.name].join("."), sm as unknown as ProcessModel, true);
  }
  // Build the structural index ONCE for the whole model, not per subject — a
  // policy over P processes was O(P·M) (a full model walk each). Shared across
  // every subject's env below.
  const indexes = buildIndexes(model);
  const cache = new Map<string, ReturnType<typeof latencyGetters> | undefined>();
  return (subjectKey: string) => {
    if (cache.has(subjectKey)) return cache.get(subjectKey);
    const hit = byKey.get(subjectKey);
    let value: ReturnType<typeof latencyGetters> | undefined;
    try {
      value = hit
        ? latencyGetters(estimateProcess(hit.model, buildLatencyEnv(model, hit.model, indexes), { fragment: hit.fragment }))
        : undefined;
    } catch {
      // A latency-fold failure must not take down the whole policy/diagnostic
      // run — it degrades to neutral (undefined) for this subject only.
      value = undefined;
    }
    cache.set(subjectKey, value);
    return value;
  };
}
